import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { supabase } from './supabase';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isTester, setIsTester] = useState(false);

  useEffect(() => {
    let mounted = true;
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id;
      if (!uid) { if (mounted) setIsTester(false); return; }
      supabase.from('user_preferences').select('user_role').eq('user_id', uid).maybeSingle()
        .then(({ data }) => {
          if (!mounted) return;
          const role = (data?.user_role ?? '').toLowerCase();
          setIsTester(role === 'tester' || role === 'beta_tester' || role === 'qa');
        })
        .catch(() => { if (mounted) setIsTester(false); });
    });
    return () => { mounted = false; listener.subscription.unsubscribe(); };
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    // Testers: keep error toasts visible until dismissed so they can screenshot/report them.
    if (type === 'error' && isTester) return;
    const duration = type === 'error' ? 10000 : 3000;
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, [isTester]);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const icons = {
    success: <CheckCircle className="h-5 w-5 text-primary-600" />,
    error: <AlertCircle className="h-5 w-5 text-error-600" />,
    info: <Info className="h-5 w-5 text-secondary-600" />,
  };

  const bg = {
    success: 'glass-card border-primary-200',
    error: 'bg-white border-error-500/40',
    info: 'glass-card border-secondary-200',
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 max-w-sm">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 px-4 py-3 shadow-lg border ${bg[toast.type]} animate-slide-up`}
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            {icons[toast.type]}
            <span className={`text-sm font-medium flex-1 ${toast.type === 'error' ? 'text-slate-900' : 'text-neutral-200'}`}>{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-neutral-500 hover:text-neutral-300 transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export function copyToClipboard(text: string, showToast?: (msg: string, type?: ToastType) => void) {
  navigator.clipboard.writeText(text).then(
    () => showToast?.('Copied to clipboard', 'success'),
    () => showToast?.('Failed to copy', 'error')
  );
}
